import { CSSProperties, ReactNode, useEffect, useId, useState } from 'react';
import {
  DragDropContext,
  Droppable,
  DroppableProps,
  OnDragEndResponder,
  Draggable,
  DropResult,
  DraggableProps,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';

export type { DropResult };

function StrictModeDroppable({ children, ...props }: DroppableProps) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
}

type DragListProps = Omit<DroppableProps, 'children'> & {
  onDragEnd: OnDragEndResponder;

  children: ReactNode;

  style?: CSSProperties;
  className?: string;
};

export function DragList({
  onDragEnd,
  className,
  style,
  children,
  droppableId,
  ...props
}: DragListProps) {
  const id = useId();

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <StrictModeDroppable droppableId={droppableId || id} {...props}>
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className={className}
            style={style}
          >
            {children}
          </div>
        )}
      </StrictModeDroppable>
    </DragDropContext>
  );
}

type DragItemCustomization<Result> = Result | ((snapshot: DraggableStateSnapshot) => Result);

type DragItemProps = Omit<DraggableProps, 'children'> & {
  children: ReactNode;

  className?: DragItemCustomization<string>;
  style?: DragItemCustomization<CSSProperties>;
};

export function DragItem({ style, className, children, ...props }: DragItemProps) {
  return (
    <>
      <Draggable {...props}>
        {(provided, snapshot) => (
          <div
            className={typeof className === 'string' ? className : className?.(snapshot)}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={{
              ...(typeof style === 'function' ? style(snapshot) : style),
              ...provided.draggableProps.style,
            }}
          >
            {children}
          </div>
        )}
      </Draggable>
    </>
  );
}
